import React, { useEffect, useState } from "react";
import { X, Download, FileText, Upload } from 'lucide-react';

import Sidebar from "../../../component/includes/sidebar";
import { CreateEducationData, educationService, ServiceEducationContent, UpdateEducationData } from "../../../services/Education";

const EducationManagement = () => {
  // State Management
  const [education, setEducation] = useState<ServiceEducationContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEducation, setEditingEducation] = useState<ServiceEducationContent | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [viewerFile, setViewerFile] = useState<string | null>(null);
  const [newEducation, setNewEducation] = useState<CreateEducationData>({
    title: "",
    description: "",
  });
  const [searchTerm, setSearchTerm] = useState("");

  // File validation function
  const validatePdfFile = (file: File): boolean => {
    const validTypes = ['application/pdf'];
    if (!validTypes.includes(file.type)) {
      showError('Please upload PDF files only');
      return false;
    }
    return true;
  };

  // File Handling Functions
  const handleFileDownload = (file?: string | File) => {
    if (!file) {
      showError('No file available for download');
      return;
    }

    const fileUrl = `${process.env.REACT_APP_API_URL}/${file}`;
    const link = document.createElement('a');
    link.href = fileUrl;
    link.setAttribute('download', typeof file === 'string' ? file : file.name);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const handleShowFile = (file?: string | File) => {
    if (!file) {
      showError('No file available to show');
      return;
    }

    const fileUrl = `${process.env.REACT_APP_API_URL}/${file}`;
    setViewerFile(fileUrl);
  }

  const closeFileViewer = () => {
    setViewerFile(null);
  }

  // File Viewer Modal Component
  const FileViewerModal = () => {
    if (!viewerFile) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] p-6">
          <button 
            onClick={closeFileViewer}
            className="absolute top-4 right-4 text-gray-600 hover:text-gray-900"
          >
            <X size={24} />
          </button>

          <div className="w-full h-full flex flex-col items-center">
            <iframe 
              src={viewerFile} 
              className="w-full h-[70vh]" 
              title="PDF Viewer"
            />

            <button 
              onClick={() => handleFileDownload(viewerFile)}
              className="mt-4 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded inline-flex items-center"
            >
              <Download size={20} className="mr-2" />
              Download PDF
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Data Fetching
  const fetchEducation = async () => {
    try {
      setLoading(true);
      const response = await educationService.getEducationalContent();
      setEducation(response);
    } catch (err) {
      showError("Failed to fetch educational content");
      console.error("Error fetching educational content:", err);
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  };

  useEffect(() => {
    fetchEducation();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Utility Functions
  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const showError = (message: string) => {
    setError(message);
    setTimeout(() => setError(null), 3000);
  };

  const resetForm = () => {
    setNewEducation({ title: "", description: "" });
    setEditingEducation(null);
    setIsModalOpen(false);
    setSelectedFile(null);
  };

  // CRUD Operations
  const handleCreateEducation = async () => {
    if (!newEducation.title.trim() || !newEducation.description.trim()) {
      showError("Title and description cannot be empty");
      return;
    }

    if (selectedFile && !validatePdfFile(selectedFile)) {
      return;
    }
  
    setLoading(true);
    try {
      const educationData: CreateEducationData = {
        title: newEducation.title,
        description: newEducation.description,
        file: selectedFile || undefined
      };
      await educationService.createEducationalContent(educationData);
      await fetchEducation();
      showSuccess("Educational content created successfully");
      resetForm();
    } catch (err) {
      showError("Failed to create educational content");
      console.error("Error creating educational content:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEducation = async () => {
    if (!editingEducation?.title.trim() || !editingEducation?.description.trim()) {
      showError("Title and description cannot be empty");
      return;
    }

    if (selectedFile && !validatePdfFile(selectedFile)) {
      return;
    }
  
    setLoading(true);
    try {
      const updatedData: UpdateEducationData = {
        id: editingEducation.id,
        title: editingEducation.title,
        description: editingEducation.description,
        file: selectedFile || undefined
      };
      await educationService.updateEducationalContent(updatedData);
      await fetchEducation();
      showSuccess("Educational content updated successfully");
      resetForm();
    } catch (err) {
      showError("Failed to update educational content");
      console.error("Error updating educational content:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEducation = async (educationId: number) => {
    if (!window.confirm("Are you sure you want to delete this educational content?"))
      return;

    setLoading(true);
    try {
      await educationService.deleteEducationalContent(educationId);
      await fetchEducation();
      showSuccess("Educational content deleted successfully");
    } catch (err) {
      showError("Failed to delete educational content");
      console.error("Error deleting educational content:", err);
    } finally {
      setLoading(false);
    }
  };

  // Filtered Data
  const filteredEducation = education.filter(
    (item) =>
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Loading Component
  const LoadingSpinner = () => (
    <div className="fixed inset-0 bg-white flex items-center justify-center z-[9999]">
      <div className="flex flex-col items-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500 border-solid mb-4"></div>
        <p className="text-gray-600 text-lg">Loading educational content...</p>
      </div>
    </div>
  );

  if (pageLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-[9999]">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      )}

      <div className="fixed top-4 right-4 z-50 space-y-2">
        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative">
            <strong className="font-bold">Success!</strong>
            <p className="block sm:inline ml-2">{successMessage}</p>
          </div>
        )}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
            <strong className="font-bold">Error!</strong>
            <p className="block sm:inline ml-2">{error}</p>
          </div>
        )}
      </div>

      <div className="flex flex-col md:flex-row">
        <Sidebar />
        <main className="flex-1 p-4 md:p-6 mt-10">
          <div className="max-w-8xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                Educational Content Management
              </h1>
              <button
                onClick={() => setIsModalOpen(true)}
                className="w-full md:w-auto bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
              >
                Add New Content
              </button>
            </div>

            <input
              type="text"
              placeholder="Search educational content..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg mb-6 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left">ID</th>
                      <th className="px-4 py-3 text-left">Title</th>
                      <th className="px-4 py-3 text-left">Description</th>
                      <th className="px-4 py-3 text-left hidden md:table-cell">PDF File</th>
                      <th className="px-4 py-3 text-left hidden md:table-cell">Created At</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEducation.map((item) => (
                      <tr
                        key={item.id}
                        className="border-b hover:bg-gray-50"
                      >
                        <td className="px-4 py-3">{item.id}</td>
                        <td className="px-4 py-3">
                          <div className="max-w-xs md:max-w-md truncate">
                            {item.title}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="max-w-xs md:max-w-md truncate">
                            {item.description}
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          {item.file && (
                            <div className="flex space-x-2">
                              <button 
                                onClick={() => handleShowFile(item.file)}
                                className="text-green-500 hover:underline flex items-center"
                              >
                                <FileText size={16} className="mr-1" /> View PDF
                              </button>
                              <button 
                                onClick={() => handleFileDownload(item.file)}
                                className="text-blue-500 hover:underline flex items-center"
                              >
                                <Download size={16} className="mr-1" /> Download
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          {new Date(item.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => {
                                setEditingEducation(item);
                                setIsModalOpen(true);
                              }}
                              className="text-blue-500 hover:underline"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteEducation(item.id)}
                              className="text-red-500 hover:underline"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </main>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-all duration-300">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-200">
            <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <h2 className="text-2xl font-bold text-gray-800">
                {editingEducation ? "Edit Educational Content" : "Add New Educational Content"}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-8 overflow-y-auto">
              <form onSubmit={(e) => {
                e.preventDefault();
                editingEducation ? handleUpdateEducation() : handleCreateEducation();
              }} className="space-y-6">
                
                <div>
                  <label htmlFor="title" className="block text-sm font-semibold text-gray-700 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    id="title"
                    value={editingEducation ? editingEducation.title : newEducation.title}
                    onChange={(e) => editingEducation 
                      ? setEditingEducation({...editingEducation, title: e.target.value}) 
                      : setNewEducation({...newEducation, title: e.target.value})
                    }
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all shadow-sm"
                    placeholder="Enter content title..."
                  />
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-semibold text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    id="description"
                    value={editingEducation ? editingEducation.description : newEducation.description}
                    onChange={(e) => editingEducation
                      ? setEditingEducation({...editingEducation, description: e.target.value})
                      : setNewEducation({...newEducation, description: e.target.value})
                    }
                    required
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all shadow-sm resize-y"
                    placeholder="Provide a brief description..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    PDF Document <span className="text-red-500">*</span>
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:bg-gray-50 hover:border-blue-400 transition-all cursor-pointer relative group">
                    <input
                      type="file"
                      id="file"
                      accept=".pdf,application/pdf"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          if (validatePdfFile(file)) {
                            setSelectedFile(file);
                          } else {
                            e.target.value = ''; // Reset input if validation fails
                            setSelectedFile(null);
                          }
                        }
                      }}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className="flex flex-col items-center justify-center pointer-events-none">
                      {selectedFile ? (
                        <>
                          <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-3">
                            <FileText size={32} />
                          </div>
                          <p className="text-sm font-medium text-gray-800 break-all px-4">{selectedFile.name}</p>
                          <p className="text-xs text-gray-500 mt-1">Click to replace file</p>
                        </>
                      ) : (
                        <>
                          <div className="w-16 h-16 bg-gray-100 text-gray-400 rounded-full flex items-center justify-center mb-3 group-hover:bg-blue-50 group-hover:text-blue-500 transition-colors">
                            <Upload size={32} />
                          </div>
                          <p className="text-sm font-medium text-gray-600">
                            <span className="text-blue-600 font-semibold">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-xs text-gray-400 mt-2">PDF files only (max 10MB)</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-gray-100 flex justify-end gap-4 mt-8">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-6 py-2.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors shadow-sm shadow-blue-200 flex items-center gap-2"
                  >
                    <Upload size={18} />
                    {editingEducation ? "Update Content" : "Create Content"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* File Viewer Modal */}
      <FileViewerModal />
    </div>
  );
};

export default EducationManagement;